import { useState } from "react";
import { updatePalletTripNote } from "../../services";
import { PalletTripRow, PalletTripSummary } from "../../types/pallet-summary.type";
import DepositNoteModal, { NoteContext } from "../SaleDepositReport/DepositNoteModal";
import NoteCell from "../SaleDepositReport/NoteCell";
import PalletTable, { PalletColumn } from "./PalletTable";
import { EMPTY_VALUE, NOTE_LABEL, NOTE_MAX_WIDTH, UNEXPLAINED_TEXT } from "./constants";
import {
  formatBusinessDate,
  formatDepartureTime,
  formatNames,
  formatPalletCount,
  formatReturnTime,
  routeLabel,
} from "./palletSummary";

const LEAD_COLUMNS: PalletColumn<PalletTripRow>[] = [
  { label: "วันที่", render: (trip) => formatBusinessDate(trip.business_date) },
  { label: "เวลาออก", render: (trip) => formatDepartureTime(trip.checked_in_at) },
  {
    label: "เวลากลับ",
    render: (trip) => formatReturnTime(trip.arrived_at, trip.business_date),
  },
  { label: "พนักงานขาย", render: (trip) => formatNames(trip.salespeople) },
  { label: "รถ", render: (trip) => trip.vehicle_plate ?? EMPTY_VALUE },
];

const NOTE_HINT = "ระบุสาเหตุที่ถาดหายหรือถาดเกิน";
const NOTE_PLACEHOLDER = "เช่น ลูกค้ายืมถาด 2 ใบ จะคืนรอบหน้า";
const CONTEXT_SEPARATOR = " · ";

const tripKey = (trip: PalletTripRow) => trip.checkin_uuid;

/** The trip the หมายเหตุ dialog is about, and its figures. */
const noteContextOf = (
  route: PalletTripSummary["route"],
  trip: PalletTripRow,
): NoteContext => ({
  title: [routeLabel(route), formatBusinessDate(trip.business_date)].join(CONTEXT_SEPARATOR),
  detail: [
    `ถาดขึ้น ${formatPalletCount(trip.pallet_out)}`,
    `ถาดกลับ ${formatPalletCount(trip.pallet_in)}`,
    `ส่วนต่าง ${formatPalletCount(trip.pallet_diff)}`,
  ].join(CONTEXT_SEPARATOR),
});

interface TripTableProps {
  report: PalletTripSummary;
  /** The branch explains its own trays; everyone else only reads the หมายเหตุ. */
  canWriteNote: boolean;
  /** A note changes what the trips and their route show, so the page asks for both again. */
  onNoteSaved: () => void;
}

/** One route — one row per trip, the lowest level, so no รวม row and nothing to open. */
export default function TripTable({
  report,
  canWriteNote,
  onNoteSaved,
}: Readonly<TripTableProps>) {
  // The trip outlives the dialog's open flag, so the fade-out still shows the trip it was about
  // instead of losing its context box mid-transition.
  const [noteTrip, setNoteTrip] = useState<PalletTripRow | null>(null);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);

  const openNote = (trip: PalletTripRow) => () => {
    setNoteTrip(trip);
    setIsNoteOpen(true);
  };
  const closeNote = () => setIsNoteOpen(false);

  const saveNote = async (note: string) => {
    if (!noteTrip) return;
    setIsSavingNote(true);
    try {
      // An emptied note is sent as null, which clears it.
      await updatePalletTripNote(noteTrip.checkin_uuid, note || null);
    } catch {
      // The interceptor has already reported the failure; the dialog stays open on the text.
      return;
    } finally {
      setIsSavingNote(false);
    }
    closeNote();
    onNoteSaved();
  };

  // A มีส่วนต่าง trip with no note is owed one: the writer is offered เพิ่มหมายเหตุ, a reader is
  // told it is missing. Any other trip without a note has nothing to say.
  const renderNote = (trip: PalletTripRow) => {
    const isUnexplained = !trip.note && trip.status === "MISMATCHED";
    if (!canWriteNote) {
      return (
        <NoteCell
          note={trip.note}
          pendingText={isUnexplained ? UNEXPLAINED_TEXT : undefined}
          maxWidth={NOTE_MAX_WIDTH}
        />
      );
    }
    if (!trip.note && !isUnexplained) return null;
    return <NoteCell note={trip.note} onEdit={openNote(trip)} maxWidth={NOTE_MAX_WIDTH} />;
  };

  const trailingColumns: PalletColumn<PalletTripRow>[] = [
    { label: NOTE_LABEL, render: renderNote },
  ];
  const noteContext = noteTrip ? noteContextOf(report.route, noteTrip) : undefined;

  return (
    <>
      <PalletTable
        leadColumns={LEAD_COLUMNS}
        trailingColumns={trailingColumns}
        rows={report.rows}
        rowKey={tripKey}
      />
      <DepositNoteModal
        open={isNoteOpen}
        note={noteTrip?.note}
        loading={isSavingNote}
        context={noteContext}
        hint={NOTE_HINT}
        placeholder={NOTE_PLACEHOLDER}
        onClose={closeNote}
        onSave={saveNote}
      />
    </>
  );
}
